import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as visitsApi from '@/api/visits';
import type { VisitFilters, VisitFormData, BulkAjutoareFormData } from '@/types/visit';

const VISITS_KEY = ['visits'] as const;
const FAMILIES_KEY = ['families'] as const;
const MAP_KEY = ['map'] as const;

function invalidateAllVisitQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: [...VISITS_KEY, 'list'] });
  // Invalidate ALL family visit queries (any familyId)
  void queryClient.invalidateQueries({ queryKey: [...FAMILIES_KEY, 'visits'] });
}

export function useVisits(filters?: VisitFilters, enabled = true) {
  return useQuery({
    queryKey: [...VISITS_KEY, 'list', filters],
    queryFn: () => visitsApi.getVisits(filters),
    enabled,
  });
}

export function useVisit(id: string | undefined) {
  return useQuery({
    queryKey: [...VISITS_KEY, 'detail', id],
    queryFn: () => visitsApi.getVisit(id!),
    enabled: !!id,
  });
}

export function useCreateVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: VisitFormData) => visitsApi.createVisit(data),
    onSuccess: () => {
      invalidateAllVisitQueries(queryClient);
    },
  });
}

export function useUpdateVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<VisitFormData> }) =>
      visitsApi.updateVisit(id, data),
    onSuccess: () => {
      invalidateAllVisitQueries(queryClient);
    },
  });
}

export function useDeleteVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => visitsApi.deleteVisit(id),
    onSuccess: () => {
      invalidateAllVisitQueries(queryClient);
    },
  });
}

export function useBulkCreateAjutoare() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkAjutoareFormData) => visitsApi.bulkCreateAjutoare(data),
    onSuccess: () => {
      invalidateAllVisitQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: MAP_KEY });
    },
  });
}
